import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { toast } from '../components/Toast';
import './UserList.css';

function UserList() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  useEffect(() => {
    let filtered = [...users];
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (roleFilter) {
      filtered = filtered.filter(user => user.role === roleFilter);
    }
    if (statusFilter !== '') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(user => user.active === isActive);
    }
    setFilteredUsers(filtered);
    setCurrentPage(0);
  }, [users, searchTerm, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/users');
      // Backend returns data wrapped in ApiResponse with nested structure
      const userData = response.data.data?.content || response.data.content || response.data;
      setUsers(userData);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const FALLBACK_ROLES = [
    { id: 1, name: 'COMPANY_ADMIN' },
    { id: 2, name: 'BRANCH_MANAGER' },
    { id: 3, name: 'SUPERVISOR' },
    { id: 4, name: 'EMPLOYEE' },
  ];

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/roles');
      const roleData = response.data.data || response.data;
      setRoles(Array.isArray(roleData) && roleData.length > 0 ? roleData : FALLBACK_ROLES);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      setRoles(FALLBACK_ROLES);
    }
  };



  const getRoleBadgeClass = (role) => {
    const roleClasses = {
      COMPANY_ADMIN: 'badge-admin',
      BRANCH_MANAGER: 'badge-manager',
      SUPERVISOR: 'badge-supervisor',
      EMPLOYEE: 'badge-employee'
    };
    return roleClasses[role] || '';
  };

  return (
    <div className="user-list-container">
      <div className="page-header">
        <h1>Users (Login Accounts)</h1>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Roles</option>
            {roles.map(role => (
              <option key={role.id} value={role.name}>
                {role.name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading users...</div>
      ) : (
        <div className="table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">No users found</td>
                </tr>
              ) : (
                filteredUsers.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage).map((user) => (
                  <tr key={user.id}>
                    <td data-label="Name"><strong>{user.name}</strong></td>
                    <td data-label="Email">{user.email}</td>
                    <td data-label="Role">
                      <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                        {user.customRoleName || user.role}
                      </span>
                    </td>
                    <td data-label="Status">
                      <span className={`status-badge ${user.active ? 'status-active' : 'status-inactive'}`}>
                        {user.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td data-label="Created At">{new Date(user.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {Math.ceil(filteredUsers.length / itemsPerPage) > 1 && (
            <div className="pagination" style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
              <button
                className="btn btn-sm"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage + 1} of {Math.ceil(filteredUsers.length / itemsPerPage)} ({filteredUsers.length} total)
              </span>
              <button
                className="btn btn-sm"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredUsers.length / itemsPerPage) - 1, prev + 1))}
                disabled={currentPage >= Math.ceil(filteredUsers.length / itemsPerPage) - 1}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UserList;
